import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import VideoGrid from '../components/VideoGrid';
import LiveChat from '../components/LiveChat';
import ModerationPanel from '../components/ModerationPanel';
import GameLobby from '../components/games/GameLobby';
import webRTCService from '../services/webrtcService';
import { Shield, LogOut, Gamepad2, MessageSquare, Users, AlertTriangle } from 'lucide-react';
import { reactionEmojis } from '../data/mockData';

export default function LiveRoomPage() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const roomTitle = location.state?.title || 'Room';
  const roomTags = React.useMemo(() => location.state?.tags || [], [location.state?.tags]);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [chatMessages, setChatMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [showGames, setShowGames] = useState(false);
  const [showModeration, setShowModeration] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [roomUsers, setRoomUsers] = useState([]);
  const [creatorId, setCreatorId] = useState('');
  const [mutedUsers, setMutedUsers] = useState([]);
  const reactionIdRef = useRef(0);
  const localStreamRef = useRef(null);

  const isCreator = user?.id === creatorId;

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      webRTCService.disconnect();
    };
  }, []);

  const initStream = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(s);
      localStreamRef.current = s;
      return s;
    } catch {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        setLocalStream(s);
        localStreamRef.current = s;
        return s;
      } catch {
        return null;
      }
    }
  }, []);

  const connectWebRTC = useCallback(async (stream) => {
    if (!user || !roomId) return;

    webRTCService.onRemoteStream = (userId, rs) => {
      setRemoteStreams(prev => new Map(prev).set(userId, rs));
    };
    webRTCService.onRemoteStreamRemoved = (userId) => {
      setRemoteStreams(prev => { const n = new Map(prev); n.delete(userId); return n; });
    };
    webRTCService.onUserJoined = (info) => {
      setRoomUsers(prev => [...prev, info]);
    };
    webRTCService.onUserLeft = (userId) => {
      setRoomUsers(prev => prev.filter(u => u.id !== userId));
      setRemoteStreams(prev => { const n = new Map(prev); n.delete(userId); return n; });
    };
    // Chat: only from server broadcast, no local add
    webRTCService.onChatMessage = (msg) => {
      setChatMessages(prev => [...prev, msg]);
    };
    webRTCService.onReaction = (msg) => {
      const id = reactionIdRef.current++;
      setReactions(prev => [...prev, { id, emoji: msg.emoji, user: msg.user_name }]);
      setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2000);
    };
    webRTCService.onRoomState = (state) => {
      setRoomUsers(state.users || []);
      if (state.creator_id) setCreatorId(state.creator_id);
      if (state.muted_users) setMutedUsers(state.muted_users);
    };
    webRTCService.onConnectionStateChange = () => {};
    webRTCService.onError = () => {};

    // Handle system messages and moderation
    const origHandler = webRTCService.handleSignalingMessage.bind(webRTCService);
    webRTCService.handleSignalingMessage = async (message) => {
      if (message.type === 'kicked') {
        alert('You have been kicked from this room.');
        leaveRoom();
        return;
      }
      if (message.type === 'user_muted') {
        setMutedUsers(prev => [...prev, message.user_id]);
        return;
      }
      if (message.type === 'user_unmuted') {
        setMutedUsers(prev => prev.filter(id => id !== message.user_id));
        return;
      }
      if (message.type === 'user_kicked' || message.type === 'user_banned') {
        setRoomUsers(prev => prev.filter(u => u.id !== message.user_id));
        setRemoteStreams(prev => { const n = new Map(prev); n.delete(message.user_id); return n; });
        return;
      }
      if (message.type === 'system') {
        return; // System messages handled silently
      }
      await origHandler(message);
    };

    try {
      await webRTCService.connect(roomId, user.id, {
        displayName: user.display_name || user.username,
        avatar: user.avatar,
        roomTitle,
        tags: roomTags
      }, stream);
    } catch (err) {
      console.error('Failed to connect:', err);
    }
  }, [user, roomId, roomTitle, roomTags]);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    let mounted = true;
    const init = async () => {
      const stream = await initStream();
      if (mounted) await connectWebRTC(stream);
    };
    init();
    return () => { mounted = false; };
  }, [user, navigate, initStream, connectWebRTC]);

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(t => { t.enabled = isMuted; });
      setIsMuted(!isMuted);
      webRTCService.sendMuteState('mute_audio', !isMuted);
    }
  };

  const toggleCamera = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(t => { t.enabled = isCameraOff; });
      setIsCameraOff(!isCameraOff);
      webRTCService.sendMuteState('mute_video', !isCameraOff);
    }
  };

  // Chat: only send via WebSocket, don't add locally (server broadcasts back)
  const sendChat = (message) => {
    webRTCService.sendChat(message);
  };

  const sendReaction = (emoji) => {
    webRTCService.sendReaction(emoji);
    const id = reactionIdRef.current++;
    setReactions(prev => [...prev, { id, emoji, user: user.display_name }]);
    setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2000);
  };

  const leaveRoom = useCallback(() => {
    webRTCService.disconnect();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    navigate('/home');
  }, [navigate]);

  // Room creator actions
  const muteUser = (targetUserId) => {
    if (webRTCService.ws?.readyState === WebSocket.OPEN) {
      webRTCService.ws.send(JSON.stringify({ type: 'mute_user', target_user_id: targetUserId }));
    }
  };
  const unmuteUser = (targetUserId) => {
    if (webRTCService.ws?.readyState === WebSocket.OPEN) {
      webRTCService.ws.send(JSON.stringify({ type: 'unmute_user', target_user_id: targetUserId }));
    }
  };
  const kickUser = (targetUserId) => {
    if (webRTCService.ws?.readyState === WebSocket.OPEN) {
      webRTCService.ws.send(JSON.stringify({ type: 'kick_user', target_user_id: targetUserId }));
    }
  };
  const banUser = (targetUserId) => {
    if (webRTCService.ws?.readyState === WebSocket.OPEN) {
      webRTCService.ws.send(JSON.stringify({ type: 'ban_user', target_user_id: targetUserId }));
    }
  };

  return (
    <div data-testid="live-room-page" className="h-screen bg-[#050108] flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 glass border-b border-white/5" style={{ position: 'relative', zIndex: 10000 }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full live-dot" />
            <span className="text-sm font-semibold text-emerald-400">ACTIVE</span>
          </div>
          <h2 className="font-semibold text-lg truncate max-w-xs">{roomTitle}</h2>
          <div className="flex items-center gap-1 text-sm text-slate-400">
            <Users className="w-4 h-4" />
            <span>{roomUsers.length}</span>
          </div>
          {isCreator && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">Creator</span>}
        </div>
        <div className="flex items-center gap-2">
          {roomTags.map(tag => (<span key={tag} className="tag-pill text-xs">{tag}</span>))}
          <button data-testid="leave-room-btn" onClick={leaveRoom}
            className="ml-3 px-4 py-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2 text-sm font-medium">
            <LogOut className="w-4 h-4" /> Leave Room
          </button>
        </div>
      </div>

      {/* Safety banner */}
      <div className="safety-banner px-4 py-2 flex items-center gap-2 text-xs text-emerald-400">
        <Shield className="w-4 h-4" />
        <span>{t('safetyReminder')}</span>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col">
          <div className="flex-1 p-4">
            <VideoGrid localStream={localStream} remoteStreams={remoteStreams} isMuted={isMuted} isCameraOff={isCameraOff}
              onToggleMute={toggleMute} onToggleCamera={toggleCamera} userName={user?.display_name || user?.username}
              roomUsers={roomUsers} />
          </div>

          <div className="fixed bottom-20 left-4 pointer-events-none z-20">
            {reactions.map(r => (<div key={r.id} className="reaction-float text-3xl">{r.emoji}</div>))}
          </div>

          <div className="flex items-center justify-between px-4 py-3 glass border-t border-white/5" style={{ position: 'relative', zIndex: 10000 }}>
            <div className="flex items-center gap-2">
              {reactionEmojis.slice(0, 6).map(emoji => (
                <button key={emoji} onClick={() => sendReaction(emoji)}
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center text-xl hover:scale-110">
                  {emoji}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="toggle-chat-btn" onClick={() => { setShowChat(!showChat); setShowGames(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${showChat ? 'bg-[#FF007F]/20 text-[#FF007F]' : 'bg-white/5 hover:bg-white/10'}`}>
                <MessageSquare className="w-4 h-4" />{t('chat')}
              </button>
              <button data-testid="toggle-games-btn" onClick={() => { setShowGames(!showGames); setShowChat(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${showGames ? 'bg-[#FF007F]/20 text-[#FF007F]' : 'bg-white/5 hover:bg-white/10'}`}>
                <Gamepad2 className="w-4 h-4" />{t('games')}
              </button>
              <button data-testid="toggle-moderation-btn" onClick={() => setShowModeration(true)}
                className="px-4 py-2 rounded-full text-sm font-medium bg-white/5 hover:bg-white/10 flex items-center gap-2 transition-all">
                <AlertTriangle className="w-4 h-4" />{t('report')}
              </button>
            </div>
          </div>
        </div>

        {showChat && (
          <div className="w-80 border-l border-white/5 flex flex-col" style={{ position: 'relative', zIndex: 10000 }}>
            <LiveChat messages={chatMessages} onSend={sendChat} />
          </div>
        )}
        {showGames && (
          <div className="w-96 border-l border-white/5 flex flex-col overflow-y-auto" style={{ position: 'relative', zIndex: 10000 }}>
            <GameLobby roomId={roomId} isHost={isCreator} />
          </div>
        )}
      </div>

      {showModeration && (
        <ModerationPanel
          onClose={() => setShowModeration(false)}
          roomUsers={roomUsers}
          isCreator={isCreator}
          mutedUsers={mutedUsers}
          onMute={muteUser}
          onUnmute={unmuteUser}
          onKick={kickUser}
          onBan={banUser}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}
